import React from 'react'

import CategoryTab from '../../pages/Tasks/Tabs/CategoryTab'
import DashboardTab from '../../pages/Tasks/Tabs/DashboardTab'
import AllTasksTab from '../../pages/Tasks/Tabs/AllTasksTab'
import CardsTab from '../../pages/Tasks/Tabs/CardsTab'

import { TaskPageStateReducer, initialState, Actions } from './reducer'
import { loadSettings } from './reducer/helpers'
import { saveTaskActivity } from '@SonarCX/lib/idb'
import { useEnvironment } from '../EnvironmentProvider'

export { ALL_COLUMNS, COLUMN_KEYS } from './reducer/helpers'
export const TASK_INCLUDE_FIELD_LIMIT = 5
export const TASK_FIELD_LIMIT = 5

type TaskPageStateContextType = {
  tab: number
  setTab: React.Dispatch<React.SetStateAction<number>>
  lastViewed: string | null
  setLastViewed: React.Dispatch<React.SetStateAction<string | null>>
  update: (key: string) => (order: string[], visible: string[]) => void
  getColumnSettings: (key: string) => { order: string[]; visible: string[] }
  lastViewedColumn: string | null
  setLastViewedColumn: React.Dispatch<React.SetStateAction<string | null>>
  lastViewedCard: string | null
  setLastViewedCard: React.Dispatch<React.SetStateAction<string | null>>
}

const TaskPageStateContext = React.createContext<TaskPageStateContextType>(null)

export const DASHBOARD_TAB = 0
export const CATEGORY_TAB = 1
export const ALL_TAB = 2
export const CARDS_TAB = 3
export const TABS = [DashboardTab, CategoryTab, AllTasksTab, CardsTab]

const TAB_KEY = 'TASK_TAB'

const TaskPageStateProvider = ({ children }) => {
  const { user, env, loaded } = useEnvironment()
  const [tab, setTab] = React.useState(+(localStorage.getItem(TAB_KEY) ?? 0))
  const [lastViewed, setLastViewed] = React.useState(null)
  const [lastViewedColumn, setLastViewedColumn] = React.useState(null)
  const [lastViewedCard, setLastViewedCard] = React.useState(null)
  const [columnState, dispatch] = React.useReducer(
    TaskPageStateReducer,
    initialState
  )

  React.useEffect(() => {
    localStorage.setItem(TAB_KEY, tab.toString())
  }, [tab])

  React.useEffect(() => {
    const pushLastViewed = async () => {
      await saveTaskActivity(user.id, env.name, lastViewed)
    }

    if (loaded && lastViewed) pushLastViewed()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastViewed, loaded])

  const update: TaskPageStateContextType['update'] = React.useCallback(
    key => (visible, order) => {
      // @ts-ignore
      dispatch({ type: Actions.UPDATE_ENTRY, key, order, visible })
    },
    []
  )

  const getColumnSettings: TaskPageStateContextType['getColumnSettings'] =
    React.useCallback(
      key => {
        if (key in columnState) {
          return columnState[key]
        }

        return loadSettings(key)
      },
      [columnState]
    )

  return (
    <TaskPageStateContext.Provider
      value={{
        tab,
        setTab,
        lastViewed,
        setLastViewed,
        update,
        getColumnSettings,
        lastViewedColumn,
        setLastViewedColumn,
        lastViewedCard,
        setLastViewedCard
      }}
    >
      {children}
    </TaskPageStateContext.Provider>
  )
}

export default TaskPageStateProvider

export const useTaskPageState = () => React.useContext(TaskPageStateContext)
